import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, api } from '../context/AuthContext';
import MessageIndicator from './MessageIndicator';
import { Menu, X } from 'lucide-react';

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [brands, setBrands] = useState([]);

  useEffect(() => {
    fetchBrands();
  }, []);

  const fetchBrands = async () => {
    try {
      const apiUrl = `/api`;
      const res = await fetch(`${apiUrl}/brands/visible`);
      const data = await res.json();
      setBrands(data.brands || []);
    } catch {
      setBrands([]);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (search.trim()) navigate(`/products?search=${encodeURIComponent(search)}`);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    setShowUserMenu(false);
  };

  return (
    <header className="sticky top-0 z-50">
      {/* Promo banner - red */}
      <div className="bg-primary text-white text-xs py-1.5 text-center font-semibold tracking-wide uppercase">
        🏍️ Envíos a todo Costa Rica &nbsp;|&nbsp; Vendedores verificados &nbsp;|&nbsp; Miles de repuestos
      </div>

      {/* Admin Banner */}
      {user?.role === 'ADMIN' && (
        <div className="bg-purple-700 text-white py-1.5">
          <div className="max-w-7xl mx-auto px-4 flex justify-between items-center text-xs">
            <span className="font-bold uppercase tracking-wide">⚠️ Modo Administrador</span>
            <div className="flex gap-4">
              <Link to="/admin" className="hover:text-purple-200 font-medium">Dashboard</Link>
              <Link to="/admin/users" className="hover:text-purple-200 font-medium">Usuarios</Link>
              <Link to="/admin/products" className="hover:text-purple-200 font-medium">Productos</Link>
              <Link to="/admin/reviews" className="hover:text-purple-200 font-medium">Reseñas</Link>
              <Link to="/admin/shops" className="hover:text-purple-200 font-medium">Talleres</Link>
              <Link to="/admin/categories" className="hover:text-purple-200 font-medium">Categorías</Link>
              <Link to="/admin/downloads" className="hover:text-purple-200 font-medium">Descargas</Link>
            </div>
          </div>
        </div>
      )}

      {/* Top utility bar */}
      <div className="bg-[#1C1C1C] text-gray-400 text-xs py-1.5">
        <div className="max-w-7xl mx-auto px-4 flex justify-between items-center">
          <span>📍 Costa Rica</span>
          <div className="flex gap-4">
            <Link to="/wants" className="hover:text-white transition-colors">Busco pieza</Link>
            <Link to="/talleres" className="hover:text-white transition-colors">🔧 Talleres</Link>
          {user ? (
              <>
                <Link to="/seller/dashboard" className="hover:text-white transition-colors">Tienda 🏪</Link>
                {user.role === 'ADMIN' && (
                  <Link to="/admin" className="text-primary font-bold hover:text-red-400">Admin</Link>
                )}
              </>
            ) : (
              <Link to="/seller/products/new" className="hover:text-white transition-colors">Vender</Link>
            )}
          </div>
        </div>
      </div>

      {/* Main header - dark */}
      <div className="bg-[#111111]">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center gap-4">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 shrink-0">
              <span className="text-2xl">🔧</span>
              <span className="font-black text-xl tracking-tight text-white">MotoMarketCR</span>
            </Link>

            {/* Search */}
            <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-2xl">
              <div className="flex w-full border-2 border-gray-600 focus-within:border-primary overflow-hidden">
                <input
                  type="text"
                  placeholder="Buscar repuestos, marca, modelo..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="flex-1 px-4 py-2.5 bg-[#2a2a2a] text-white placeholder-gray-500 focus:outline-none text-sm"
                />
                <button type="submit" className="bg-primary text-white px-5 py-2.5 font-bold uppercase text-sm tracking-wide hover:bg-primary-dark transition-colors">
                  Buscar
                </button>
              </div>
            </form>

            {/* Actions */}
            <div className="flex items-center gap-3 ml-auto">
              {user ? (
                <div className="relative">
                  <MessageIndicator />
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center gap-2 text-white"
                  >
                    <div className="w-8 h-8 bg-primary text-white flex items-center justify-center font-black text-sm">
                      {user.name ? user.name.charAt(0).toUpperCase() : '?'}
                    </div>
                    <span className="hidden md:block text-sm font-semibold text-gray-300">{user.name?.split(' ')[0]}</span>
                    <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {showUserMenu && (
                    <div className="absolute right-0 top-10 w-56 bg-white border border-gray-200 shadow-xl py-1 z-50">
                      <div className="px-4 py-3 border-b bg-gray-50">
                        <p className="font-bold text-sm">{user.name}</p>
                        <p className="text-xs text-gray-500">{user.email}</p>
                        <span className={`inline-block mt-1 px-2 py-0.5 text-xs font-bold uppercase tracking-wide ${
                          user.role === 'ADMIN' ? 'bg-purple-100 text-purple-700' :
                          user.role === 'SELLER' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {user.role === 'ADMIN' ? 'Administrador' : user.role === 'SELLER' ? 'Vendedor' : 'Comprador'}
                        </span>
                      </div>
                      <Link to="/profile" className="block px-4 py-2 text-sm hover:bg-gray-50" onClick={() => setShowUserMenu(false)}>Mi Perfil</Link>
                      <Link to="/wishlist" className="block px-4 py-2 text-sm hover:bg-gray-50" onClick={() => setShowUserMenu(false)}>❤️ Lista de Deseos</Link>
                      <Link to="/wants-chat" className="block px-4 py-2 text-sm hover:bg-gray-50" onClick={() => setShowUserMenu(false)}>💬 Mis Mensajes</Link>
                      <Link to="/wants" className="block px-4 py-2 text-sm hover:bg-gray-50" onClick={() => setShowUserMenu(false)}>🔍 Mis Solicitudes</Link>
                      <Link to="/seller/products" className="block px-4 py-2 text-sm hover:bg-gray-50" onClick={() => setShowUserMenu(false)}>📦 Mis Productos</Link>
                      <Link to="/seller/products/new" className="block px-4 py-2 text-sm hover:bg-gray-50" onClick={() => setShowUserMenu(false)}>➕ Publicar Producto</Link>
                      <Link to="/downloads" className="block px-4 py-2 text-sm hover:bg-gray-50" onClick={() => setShowUserMenu(false)}>⬇️ Descargas</Link>
                      {user.role === 'ADMIN' && (
                        <Link to="/admin" className="block px-4 py-2 text-sm text-purple-700 font-bold hover:bg-purple-50" onClick={() => setShowUserMenu(false)}>⚙️ Panel Admin</Link>
                      )}
                      <hr className="my-1" />
                      <button onClick={handleLogout} className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50">
                        Cerrar sesión
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="hidden md:flex items-center gap-2">
                  <Link to="/register" className="border border-gray-500 text-gray-300 px-4 py-2 text-sm font-semibold uppercase tracking-wide hover:border-white hover:text-white transition-colors">
                    Registrarse
                  </Link>
                  <Link to="/login" className="bg-primary text-white px-4 py-2 text-sm font-bold uppercase tracking-wide hover:bg-primary-dark transition-colors">
                    Ingresar
                  </Link>
                </div>
              )}

              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="md:hidden p-2 text-white hover:text-gray-300"
              >
                {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile search */}
          <form onSubmit={handleSearch} className="md:hidden mt-3">
            <div className="flex border-2 border-gray-600 focus-within:border-primary overflow-hidden">
              <input
                type="text"
                placeholder="Buscar..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="flex-1 px-4 py-2.5 bg-[#2a2a2a] text-white placeholder-gray-500 focus:outline-none text-sm"
              />
              <button type="submit" className="bg-primary text-white px-5 py-2.5 font-bold text-sm">
                🔍
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Categories nav - dark gray */}
      <nav className="hidden md:block bg-[#2a2a2a] border-t border-gray-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-0 overflow-x-auto">
            {brands.map(brand => (
              <Link
                key={brand}
                to={`/products?brand=${brand}`}
                className="px-4 py-2.5 text-xs font-semibold text-gray-300 hover:bg-primary hover:text-white transition-colors whitespace-nowrap uppercase tracking-wide border-r border-gray-700"
              >
                {brand}
              </Link>
            ))}
            <Link
              to="/accessories"
              className="px-4 py-2.5 text-xs font-bold text-yellow-400 hover:bg-yellow-500 hover:text-white transition-colors whitespace-nowrap uppercase tracking-wide border-r border-gray-700"
            >
              🪖 Accesorios
            </Link>
            <Link
              to="/products"
              className="px-4 py-2.5 text-xs font-bold text-primary hover:bg-primary hover:text-white transition-colors whitespace-nowrap uppercase tracking-wide"
            >
              Ver todos →
            </Link>
          </div>
        </div>
      </nav>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden bg-[#1C1C1C] border-t border-gray-700">
          <div className="p-4 space-y-1">
            <Link to="/products" className="block py-3 px-4 text-white hover:bg-[#2a2a2a] font-semibold uppercase text-sm tracking-wide">Productos</Link>
            <Link to="/accessories" className="block py-3 px-4 text-yellow-400 hover:bg-[#2a2a2a] font-semibold uppercase text-sm tracking-wide">🪖 Accesorios</Link>
            <Link to="/wants" className="block py-3 px-4 text-white hover:bg-[#2a2a2a] font-semibold uppercase text-sm tracking-wide">Busco pieza</Link>
            <Link to="/talleres" className="block py-3 px-4 text-white hover:bg-[#2a2a2a] font-semibold uppercase text-sm tracking-wide">🔧 Talleres</Link>
            {user ? (
              <>
                <Link to="/seller/dashboard" className="block py-3 px-4 text-white hover:bg-[#2a2a2a] font-semibold uppercase text-sm tracking-wide">Tienda 🏪</Link>
                <Link to="/profile" className="block py-3 px-4 text-white hover:bg-[#2a2a2a] font-semibold uppercase text-sm tracking-wide">Mi Perfil</Link>
                <Link to="/wants-chat" className="block py-3 px-4 text-white hover:bg-[#2a2a2a] font-semibold uppercase text-sm tracking-wide">💬 Mensajes</Link>
                {user.role === 'ADMIN' && (
                  <Link to="/admin" className="block py-3 px-4 bg-purple-900 text-purple-200 font-bold uppercase text-sm tracking-wide">⚙️ Panel Admin</Link>
                )}
                <button onClick={handleLogout} className="block w-full text-left py-3 px-4 text-red-400 hover:bg-[#2a2a2a] font-semibold uppercase text-sm tracking-wide">
                  Cerrar sesión
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="block py-3 px-4 bg-primary text-white text-center font-bold uppercase text-sm tracking-wide">Iniciar sesión</Link>
                <Link to="/register" className="block py-3 px-4 border border-gray-600 text-white text-center font-bold uppercase text-sm tracking-wide mt-2">Crear cuenta</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
